<template>
  <div class="ns-searchbar-list" :class="[typeClass]">
    <!-- header -->
    <p v-if="type === 'popular'" class="title-text">{{ title }}</p>
    <ul :id="type + '-search-items'" class="search-list">
      <li
        v-if="type === 'recent'"
        class="clear-recent-searches"
        @click="$emit('clear-recent-searches')"
      >
        <p class="title-text recent">
          {{ title }}
        </p>
      </li>

      <!-- searches -->
      <li
        v-for="item in searchData"
        :key="item.sku || item.itemName"
        :data-search-type="type"
        class="list-item"
        :class="{
          'show-pricing': type === 'predictive' && showPricingInPredictive
        }"
        :title="item.itemName"
        @click="$emit('click-search-item', item, type)"
        @mouseover="$emit('toggle-value', $event)"
        @mouseout="$emit('toggle-value', $event)"
      >
        <!-- image -->
        <div
          class="image-block"
          :class="{ 'two-line': showPricingInPredictive }"
        >
          <img
            :src="item.img + '.64.64.png'"
            :class="{ 'error-loading': !item.img }"
            alt="Product"
            @error="imgError"
            @load="imgLoad"
          />
          <div class="missing-image">
            <NsIcon
              class="missing-icon"
              icon-name="icon-product-placeholder-24"
              size="custom"
            />
          </div>
        </div>

        <!-- description -->
        <div v-if="showPricingInPredictive" class="list-two-line-row">
          <div class="list-row">
            <div class="item-description">
              <VClamp autoresize :max-lines="1">
                {{ item.itemName }}
              </VClamp>
            </div>
          </div>
          <div class="list-row">
            <div class="item-sku" v-html="displaySku(item)" />
            <div v-if="pricesLoading">
              <img
                class="loading-spinner"
                src="https://www.nuskin.com/content/dam/apps/checkout/icon-spinner.gif"
                alt="Loading"
              />
            </div>
            <div v-else class="item-pricing">
              <span v-if="showPVInPricing()" v-html="formatPV(item.pv)" />
              <span>&bull; </span>
              <span
                v-if="item.originalPrice && item.originalPrice > item.price"
                class="item-linethrough"
                >{{ formatPrice(item.originalPrice) }}</span
              >
              <span class="item-bold">{{ formatPrice(item.price) }}</span>
            </div>
          </div>
        </div>
        <div v-else class="item-description">
          <VClamp autoresize :max-lines="2">
            {{ item.itemName }}
          </VClamp>
        </div>

        <!-- icon -->
        <div
          v-if="showAddToCart(item)"
          class="add-to-cart-button"
          :class="{ 'two-line': showPricingInPredictive }"
          @click.stop="$emit('add-to-cart', $event, item)"
        >
          <NsIcon
            class="add-to-cart-icon"
            :icon-name="
              isExclusiveOffer(item)
                ? 'icon-exclusive-offer'
                : 'icon-smallcart-add'
            "
          />
        </div>
      </li>

      <!-- show more -->
      <li
        v-if="showMoreButton && !hideSeeMoreResults"
        class="see-more-results"
        @click="$emit('see-more-results')"
      >
        {{ translations.seeMoreResults }}
      </li>
    </ul>
  </div>
</template>

<script>
import { UserService } from "@nuskin/ns-account";
import { StringService } from "@nuskin/ns-util";
import { CurrencyService } from "@nuskin/ns-shop";
import { NsIcon } from "@nuskin/design-components";
import VClamp from "vue-clamp";

export default {
  name: "NsSearchbarList",
  components: { NsIcon, VClamp },
  props: {
    hideAddToBag: Boolean,
    hideSeeMoreResults: Boolean,
    language: {
      type: String,
      default: ""
    },
    market: {
      type: String,
      default: ""
    },
    mode: {
      type: String,
      default: ""
    },
    pricesLoading: Boolean,
    searchData: {
      type: Array,
      default: function() {
        return [];
      }
    },
    showMoreButton: Boolean,
    showPricingInPredictive: Boolean,
    title: {
      type: String,
      default: ""
    },
    translations: {
      type: Object,
      default: function() {
        return {};
      }
    },
    // one of "recent", "popular", "predictive"
    type: {
      type: String,
      default: ""
    }
  },
  data: function() {
    return {
      PSVString: "SV",
      user: UserService.getUser()
    };
  },
  computed: {
    typeClass() {
      return this.type;
    }
  },
  mounted() {
    StringService.getStrings([{ key: "psvDist", dflt: "SV" }]).then(strings => {
      this.PSVString = strings.psvDist;
    });
  },
  methods: {
    displaySku(item) {
      return item.agelocme ? item.agelocme.code : item.sku;
    },
    formatPrice(price) {
      return CurrencyService.format(price, this.market, this.language);
    },
    formatPV(pv) {
      if (typeof pv === "number") {
        return "&bull; " + this.PSVString + ":" + pv;
      } else {
        return "";
      }
    },
    getAgelocData(item) {
      return item.agelocme ? JSON.stringify(item.agelocme) : "";
    },
    hasSKU(item) {
      return typeof item.sku !== "undefined";
    },
    hasUser() {
      return this.user && this.user.id;
    },
    imgError(e) {
      e.target.classList.add("error-loading");
    },
    imgLoad(e) {
      e.target.classList.remove("error-loading");
    },
    isExclusiveOffer(item) {
      return item.isExclusive === "true" || item.isExclusive === true;
    },
    notBaseSKU(item) {
      return item.sku.substring(2, 4) !== "55";
    },
    showAddToCart(item) {
      return this.hasSKU(item) && this.notBaseSKU(item) && !this.hideAddToBag;
    },
    showPVInPricing() {
      return this.hasUser() && this.user.isDistributor();
    }
  }
};
</script>

<style lang="scss" scoped>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/global.mixins";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/other";

.ns-searchbar-list {
  display: flex;
  flex-direction: column;

  &.popular {
    background-color: map.get(colors.$themeColors, color-background-1);
  }
}

.title-text {
  margin: 14px 0;
  padding: 0 10px;
  width: 100%;
  color: map.get(colors.$themeColors, color-interactive-7);
  @include global.p-small();
}

.title-text.recent {
  padding: 12px 15px;
  margin: 0;
  color: map.get(colors.$themeColors, color-interactive-7);
  background-color: white;
  font-weight: 500;
  text-transform: uppercase;
  @include global.a-micro-caps();
  text-align: right;
  cursor: pointer;
}

.search-list {
  padding: 0;
  margin: 0;
  max-height: 280px;
  overflow-y: auto;
  transition: map.get(other.$otherUtils, global-transition);

  li {
    list-style-type: none;
    transition: map.get(other.$otherUtils, global-transition);
  }
}

.list-item {
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  align-items: center;
  padding: 0 0;
  height: 40px;
  color: map.get(colors.$themeColors, color-interactive-8);
  @include global.p-medium();
  cursor: pointer;

  &.show-pricing {
    height: 60px;
  }

  &.active {
    background-color: map.get(colors.$themeColors, color-interactive-5);
  }
}

.clear-recent-searches.active .title-text.recent {
  background-color: map.get(colors.$themeColors, color-interactive-5);
}

.image-block {
  flex-shrink: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  margin: 0 10px;
  width: 30px;
  height: 30px;
  text-align: center;

  &.two-line {
    width: 45px;
    height: 45px;
  }

  img {
    max-width: 100%;
    max-height: 100%;
    height: auto;
    width: auto;
  }
}

.missing-image {
  display: none;
  height: 100%;
  width: 100%;
  max-width: 24px;
}

.missing-icon {
  height: 100%;
  width: 100%;
}

.error-loading {
  display: none;
}

.error-loading + .missing-image {
  display: inline-block;
}

.list-two-line-row {
  width: 100%;
}

.list-row {
  display: flex;
  flex-direction: row;
  width: 100%;
}

.item-description {
  flex-grow: 1;
}

.item-sku {
  padding-right: 5px;
  font-size: small;
}

.loading-spinner {
  height: 20px;
}

.item-pricing {
  padding-right: 10px;
  width: 100%;
  font-size: small;
}

.item-bold {
  font-weight: 700;
}

.item-linethrough {
  text-decoration: line-through;
}

.add-to-cart-button {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  border-left: 1px solid map.get(colors.$themeColors, color-gray-4);
  width: 40px;
  height: 100%;
  color: map.get(colors.$themeColors, color-expressive-6);

  &.two-line {
    width: 55px;
  }
}

.add-to-cart-icon {
  color: map.get(colors.$themeColors, color-interactive-1);
}

.see-more-results {
  display: block;
  align-items: center;
  padding: 10px 0;
  border-top: 1px solid map.get(colors.$themeColors, color-gray-4);
  color: map.get(colors.$themeColors, color-interactive-7);
  text-align: center;
  @include global.p-medium();
  @include global.a-micro-caps();
  cursor: pointer;

  &.active {
    background-color: map.get(colors.$themeColors, color-interactive-5);
  }
}

@media (max-width: 1023px) {
  .list-item {
    border-top: 2px solid map.get(colors.$themeColors, color-background-1);
    background-color: white;
  }

  .add-to-cart-button.two-line {
    width: 40px;
  }

  .search-list {
    max-height: calc(100vh - 75px);
  }
}

@media (min-width: 1024px) {
  .list-item {
    border-top: 1px solid map.get(colors.$themeColors, color-gray-4);
  }

  .image-block {
    width: 30px;
    height: 30px;
  }
}
</style>
